// Creator payouts (wave 10, docs/build/CONTRACTS.md "Creator payouts (Stripe Connect Express)"): what the dashboard's
// Payouts and Earnings pages show, as plain functions over money's answers so every state can be tested.
//
// - `GET /v1/creators/me/payout-account` → `{status: none|pending|enabled|restricted, country, requirements_due}`
// - `POST /v1/creators/me/payout-account` → `{onboarding_url}` (Stripe's hosted onboarding; it returns to
//   /dashboard/payouts)
// - `GET /v1/creators/me/balance` → `{available, pending, min_cashout, can_cash_out}` (Money, settlement currency)
// - `POST /v1/creators/me/cashouts` (idempotent) and `GET /v1/creators/me/cashouts`
//
// Anything malformed reads as "don't know", never as money the creator has.

import { formatMoney, parseMoney, showMoney, type Money } from "./money.ts";

export type PayoutStatus = "none" | "pending" | "enabled" | "restricted";

export interface PayoutAccount {
  status: PayoutStatus;
  country: string | null;
  requirementsDue: string[];
}

export function parsePayoutAccount(raw: unknown): PayoutAccount | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const status = r.status;
  if (status !== "none" && status !== "pending" && status !== "enabled" && status !== "restricted") return null;
  const due = Array.isArray(r.requirements_due) ? r.requirements_due.filter((x): x is string => typeof x === "string") : [];
  return { status, country: typeof r.country === "string" && /^[A-Z]{2}$/.test(r.country) ? r.country : null, requirementsDue: due };
}

/** Stripe's requirement codes in plain words; anything else is shown tidied up. */
const REQUIREMENTS: [RegExp, string][] = [
  [/^external_account$/, "A bank account for payouts"],
  [/^tos_acceptance\./, "Accept Stripe's terms"],
  [/verification\.(additional_)?document$/, "A photo of your ID"],
  [/\.dob\./, "Your date of birth"],
  [/\.address\./, "Your address"],
  [/\.(id_number|ssn_last_4)$/, "Your tax ID number"],
  [/\.(first_name|last_name)$/, "Your legal name"],
  [/\.(email)$/, "Your email address"],
  [/\.(phone)$/, "Your phone number"],
  [/^business_profile\./, "A few details about what you sell"],
  [/^business_type$/, "Whether you're an individual or a company"],
];

export function requirementLabel(code: string): string {
  for (const [re, words] of REQUIREMENTS) if (re.test(code)) return words;
  const last = code.split(".").pop() ?? code;
  const words = last.replace(/_/g, " ").trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : code;
}

/** The distinct labels for `requirementsDue`, in order. */
export function requirementLabels(codes: string[]): string[] {
  return [...new Set(codes.map(requirementLabel))];
}

export interface StatusView {
  badge: string;
  tone: "ok" | "warn" | "dim";
  text: string;
  /** the button that opens Stripe's onboarding, or null */
  action: string | null;
  requirements: string[];
}

export function payoutStatusView(a: PayoutAccount | null): StatusView {
  if (!a) return { badge: "Unknown", tone: "dim", text: "We couldn't check your payout account right now.", action: null, requirements: [] };
  const requirements = requirementLabels(a.requirementsDue);
  switch (a.status) {
    case "none":
      return {
        badge: "Not set up",
        tone: "dim",
        text: "Banger pays creators through Stripe. Set up a payout account with your bank details to cash out what you earn.",
        action: "Set up payouts",
        requirements: [],
      };
    case "pending":
      return requirements.length
        ? { badge: "In progress", tone: "warn", text: "Your payout account isn't finished. Stripe still needs:", action: "Continue setup", requirements }
        : { badge: "Being checked", tone: "warn", text: "Stripe is checking your details. This usually takes a few minutes, sometimes a day or two.", action: null, requirements: [] };
    case "restricted":
      return {
        badge: "Needs attention",
        tone: "warn",
        text: requirements.length ? "Stripe has paused payouts until you add:" : "Stripe has paused payouts. Open your payout account to see why.",
        action: "Finish setup",
        requirements,
      };
    case "enabled":
      return { badge: "Ready", tone: "ok", text: "Your payout account is ready. Cash-outs go to your bank through Stripe.", action: null, requirements: [] };
  }
}

/** One word for the dashboard's overview tile. */
export function payoutStatusShort(a: PayoutAccount | null): string {
  return payoutStatusView(a).badge;
}

/**
 * Stripe's onboarding link from `POST /v1/creators/me/payout-account`. Only https, or a link on this site itself (a
 * dev fake), is followed; anything else is refused.
 */
export function safeOnboardingUrl(raw: unknown, siteOrigin: string = typeof location === "undefined" ? "" : location.origin): string | null {
  const s = raw && typeof raw === "object" ? (raw as { onboarding_url?: unknown }).onboarding_url : null;
  if (typeof s !== "string") return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.username || u.password) return null;
  if (u.protocol === "https:" || (siteOrigin && u.origin === siteOrigin && u.protocol === "http:")) return u.href;
  return null;
}

export interface Balance {
  available: Money;
  pending: Money;
  minCashout: Money;
  canCashOut: boolean;
}

export function parseBalance(raw: unknown): Balance | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const available = parseMoney(r.available);
  const pending = parseMoney(r.pending);
  const minCashout = parseMoney(r.min_cashout);
  if (!available || !pending || !minCashout) return null;
  return { available, pending, minCashout, canCashOut: r.can_cash_out === true };
}

export interface CashOutView {
  /** show the "Cash out" button (only when money says `can_cash_out`) */
  button: string | null;
  note: string;
}

/** The cash-out box: the button only on money's `can_cash_out`; otherwise why not. */
export function cashOutView(b: Balance | null, a: PayoutAccount | null): CashOutView {
  if (!b) return { button: null, note: "We couldn't load your balance right now." };
  const min = formatMoney(b.minCashout.amount_minor, b.minCashout.currency);
  if (b.canCashOut) return { button: `Cash out ${formatMoney(b.available.amount_minor, b.available.currency)}`, note: "It goes to your bank through Stripe, usually within a few days." };
  if (a?.status !== "enabled") return { button: null, note: `Set up payouts to cash out. You can cash out from ${min}.` };
  if (b.available.amount_minor < b.minCashout.amount_minor) return { button: null, note: `You can cash out from ${min}. Earnings become available 14 days after each sale.` };
  return { button: null, note: "Cash-out isn't available right now." };
}

export type CashoutStatus = "pending" | "paid" | "failed";

export interface Cashout {
  id: string;
  amount: Money;
  status: CashoutStatus;
  /** milliseconds since 1970 */
  createdAt: number | null;
}

/** `GET /v1/creators/me/cashouts`: newest first; malformed rows are left out. */
export function parseCashouts(raw: unknown): Cashout[] {
  const list = raw && typeof raw === "object" && Array.isArray((raw as { cashouts?: unknown }).cashouts) ? (raw as { cashouts: unknown[] }).cashouts : [];
  const out: Cashout[] = [];
  for (const x of list) {
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    const amount = parseMoney(r.amount);
    const status = r.status;
    if (typeof r.cashout_id !== "string" || !amount || (status !== "pending" && status !== "paid" && status !== "failed")) continue;
    // seconds or milliseconds since 1970, or an ISO date; kept as milliseconds
    const t = typeof r.created_at === "number" ? (r.created_at < 1e12 ? r.created_at * 1000 : r.created_at) : typeof r.created_at === "string" ? Date.parse(r.created_at) : NaN;
    out.push({ id: r.cashout_id, amount, status, createdAt: Number.isFinite(t) ? t : null });
  }
  return out.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export const CASHOUT_STATUS_LABEL: Record<CashoutStatus, string> = { pending: "On its way", paid: "Paid", failed: "Failed" };

/** What the page says after `POST /v1/creators/me/cashouts`. */
export function cashOutResult(raw: unknown): string {
  const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const amount = showMoney(r.amount, "Your cash-out");
  if (r.status === "failed") return `${amount} couldn't be sent. Check your payout account and try again.`;
  if (r.status === "paid") return `${amount} was sent to your bank.`;
  return `${amount} is on its way to your bank.`;
}

// ---- earnings in the settlement currency -------------------------------------------------------------------

export interface MonthEarnings {
  month: string;
  earned: Money;
  /** the part of `earned` that came from tips */
  tips: Money | null;
}

/**
 * `GET /v1/creators/me/earnings` → `by_month: [{month, earned: Money, tips?: Money}]` (amounts already the creator's
 * 80%, in the settlement currency). Malformed rows are left out, oldest first as money sends them.
 */
export function parseEarnings(raw: unknown): MonthEarnings[] {
  const list = raw && typeof raw === "object" && Array.isArray((raw as { by_month?: unknown }).by_month) ? (raw as { by_month: unknown[] }).by_month : [];
  const out: MonthEarnings[] = [];
  for (const x of list) {
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    const earned = parseMoney(r.earned);
    if (typeof r.month !== "string" || !earned) continue;
    const tips = parseMoney(r.tips);
    out.push({ month: r.month, earned, tips: tips && tips.amount_minor > 0 ? tips : null });
  }
  return out;
}

/** "You keep 80% of every sale and tip, after tax." from money's `/v1/config` `split.platform_bps`. */
export function shareText(cfg: unknown): string {
  const bps = cfg && typeof cfg === "object" ? (cfg as { split?: { platform_bps?: unknown } }).split?.platform_bps : undefined;
  if (typeof bps !== "number" || !Number.isInteger(bps) || bps < 0 || bps > 10000) return "";
  return `You keep ${(10000 - bps) / 100}% of every sale and tip, after tax. `;
}
